import { Link } from 'react-router-dom';
import { useMemo } from 'react';
import { BarChart3, Eye, Film, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { buildAngleGroups } from '@/hooks/useSessionWatchData';
import { useFailedImages } from '@/hooks/useFailedImages';
import { formatDurationClock } from '@/utils/duration';
import type { SessionRecordingSummary } from '@/types';

interface SessionRecordingsGridProps {
  sessionId: string;
  recordings: SessionRecordingSummary[];
  areRecordingsLoading: boolean;
  hasAvailableVideos: boolean;
  isSessionOwner: boolean;
}

// The session detail page no longer embeds a player (issue #1000): each
// recording becomes a thumbnail card that deep-links into the analytics
// tool with that recording preselected.
export default function SessionRecordingsGrid({
  sessionId,
  recordings,
  areRecordingsLoading,
  hasAvailableVideos,
  isSessionOwner,
}: SessionRecordingsGridProps) {
  const { t } = useTranslation('pages');
  const { hasFailed, markFailed } = useFailedImages();

  // Recordings sharing an angleName are parts of one camera angle; show a
  // "i/total" badge so a split angle reads as a sequence, not duplicates.
  const partParamsByRecordingId = useMemo(() => {
    const map = new Map<string, { i: number; total: number }>();
    for (const group of buildAngleGroups(recordings)) {
      if (group.fragments.length <= 1) {
        continue;
      }
      group.fragments.forEach((fragment, index) => {
        map.set(fragment.id, { i: index + 1, total: group.fragments.length });
      });
    }
    return map;
  }, [recordings]);

  if (areRecordingsLoading) {
    return (
      <div className="flex min-h-48 items-center justify-center rounded-2xl bg-black text-secondary-300">
        <Loader2
          className="h-7 w-7 animate-spin"
          aria-label={t('sessions.detail.videoFiles.preparingRecording')}
        />
      </div>
    );
  }

  if (recordings.length === 0) {
    return (
      <div className="flex min-h-48 items-center justify-center rounded-2xl bg-black p-6 text-center text-secondary-300">
        <div>
          <p className="font-medium">
            {hasAvailableVideos
              ? t('sessions.detail.videoFiles.preparingRecording')
              : t('sessions.detail.videoFiles.noRecordings')}
          </p>
          {hasAvailableVideos && (
            <p className="mt-1 text-sm text-secondary-400">
              {t('sessions.detail.videoFiles.preparingRecordingBody')}
            </p>
          )}
        </div>
      </div>
    );
  }

  const visibleRecordings = recordings.filter(
    (recording) =>
      recording.playbackStatus !== 'unavailable' || isSessionOwner,
  );

  return (
    <div
      role="list"
      className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
    >
      {visibleRecordings.map((recording, index) => {
        const isPlayable =
          recording.playbackStatus === 'ready' ||
          recording.playbackStatus === 'partial';
        const title =
          recording.angleName ??
          t('sessions.detail.videoFiles.recordingLabel', {
            index: index + 1,
          });
        const partParams = partParamsByRecordingId.get(recording.id);
        const thumbnail =
          recording.thumbnailUrl && !hasFailed(recording.thumbnailUrl)
            ? recording.thumbnailUrl
            : undefined;
        const completedChapters = recording.chapters.filter(
          (chapter) => chapter.uploadStatus === 'completed',
        ).length;

        const thumbnailArea = (
          <div
            className={`relative aspect-video overflow-hidden bg-gradient-to-br from-slate-800 via-slate-900 to-black ${
              recording.playbackStatus === 'unavailable' ? 'grayscale' : ''
            }`}
          >
            {thumbnail && (
              <img
                src={thumbnail}
                alt=""
                className="absolute inset-0 h-full w-full object-cover"
                onError={() => markFailed(thumbnail)}
              />
            )}
            {!thumbnail && isPlayable && (
              <div className="flex h-full items-center justify-center text-secondary-500">
                <Film className="h-8 w-8" aria-hidden="true" />
              </div>
            )}
            {!isPlayable && (
              <div className="flex h-full items-center justify-center">
                {recording.playbackStatus === 'pending' ? (
                  <Loader2
                    className="h-7 w-7 animate-spin text-secondary-300"
                    aria-label={t(
                      'sessions.detail.videoFiles.processingRecording',
                    )}
                  />
                ) : (
                  <Film className="h-8 w-8 text-secondary-600" aria-hidden="true" />
                )}
              </div>
            )}
            {isPlayable && (
              <span
                aria-hidden="true"
                className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
              >
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-secondary-900 shadow-lg">
                  <BarChart3 className="h-3.5 w-3.5 text-primary-500" />
                  {t('sessions.detail.videoFiles.openInAnalytics')}
                </span>
              </span>
            )}
            {partParams && (
              <span className="absolute left-2 top-2 rounded-md bg-black/75 px-2 py-1 text-xs font-medium text-slate-200">
                {t('watch.partLabel', partParams)}
              </span>
            )}
            {recording.playbackStatus === 'partial' && (
              <span className="absolute right-2 top-2 rounded-md bg-amber-600/90 px-2 py-1 text-xs font-medium text-white">
                {t('sessions.detail.videoFiles.partialRecording')}
              </span>
            )}
            {recording.playbackStatus === 'unavailable' && (
              <span className="absolute right-2 top-2 rounded-md bg-secondary-600/90 px-2 py-1 text-xs font-medium text-white">
                {t('sessions.detail.videoFiles.unavailableRecording')}
              </span>
            )}
            {recording.duration != null && (
              <span className="absolute bottom-2 right-2 rounded-md bg-black/75 px-1.5 py-0.5 text-xs font-medium tabular-nums text-slate-200">
                {formatDurationClock(recording.duration)}
              </span>
            )}
          </div>
        );

        const body = (
          <div className="flex flex-1 flex-col gap-1.5 p-3">
            <div className="flex items-baseline justify-between gap-2">
              <span className="truncate text-sm font-semibold text-secondary-900">
                {title}
              </span>
              {typeof recording.viewCount === 'number' && (
                <span className="flex flex-none items-center gap-1 text-xs text-secondary-400">
                  <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('watch.viewCount', { count: recording.viewCount })}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-secondary-500">
              <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-secondary-600">
                {t('sessions.detail.videoFiles.chapterCount', {
                  count: recording.chapters.length,
                })}
              </span>
              {recording.playbackStatus === 'pending' && (
                <span>
                  {recording.chapters.length > 0
                    ? t('sessions.detail.videoFiles.processingFiles', {
                        completed: completedChapters,
                        total: recording.chapters.length,
                      })
                    : t('sessions.detail.videoFiles.processingRecording')}
                </span>
              )}
            </div>
          </div>
        );

        if (!isPlayable) {
          return (
            <div
              key={recording.id}
              role="listitem"
              className="flex flex-col overflow-hidden rounded-lg border border-secondary-200 bg-white"
            >
              {thumbnailArea}
              {body}
            </div>
          );
        }

        // role="listitem" must live on a wrapper: putting it directly on the
        // <a> would override its implicit link role and break screen readers
        // (and getByRole('link')).
        return (
          <div key={recording.id} role="listitem" className="flex">
            <Link
              to={`/sessions/${sessionId}/analytics?recording=${recording.id}`}
              className="group flex flex-1 flex-col overflow-hidden rounded-lg border border-secondary-200 bg-white transition hover:-translate-y-px hover:border-primary-400 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
              {thumbnailArea}
              {body}
              <span className="sr-only">
                {t('sessions.detail.videoFiles.openInAnalytics')} — {title}
              </span>
            </Link>
          </div>
        );
      })}
    </div>
  );
}
